import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';

/** Default Axios timeout for API client (ms). */
export const DEFAULT_API_TIMEOUT_MS = 30_000;

/** Longer timeout for job list/search calls (`GET` / `POST /jobs`). */
export const JOBS_API_TIMEOUT_MS = 50_000;

export type ApiOptions = {
  showErrorToast?: boolean;
  /** Overrides client default timeout for this request (ms). */
  timeout?: number;
  /** Additional request headers (merged with client defaults). */
  headers?: Record<string, string>;
};

export type CreateApiClientConfig = {
  baseURL: string;
  withCredentials?: boolean;
  timeout?: number;
  /** Called on each request to inject headers (e.g. x-language) */
  getRequestHeaders?: () => Record<string, string>;
  /** Called when a request fails. Must throw. */
  handleError: (error: unknown, showToast: boolean) => never;
};

export type ApiClient = {
  get: <T = unknown>(url: string, config?: AxiosRequestConfig, options?: ApiOptions) => Promise<T>;
  post: <T, D>(url: string, data: D, options?: ApiOptions) => Promise<T>;
  patch: <T, D>(url: string, data: D, options?: ApiOptions) => Promise<T>;
  delete: <T = unknown>(url: string, options?: ApiOptions) => Promise<T>;
};

/** Check if error is an abort/cancel (should be rethrown without handling) */
export function isAbortError(error: unknown): boolean {
  if (axios.isCancel(error)) return true;
  if (error instanceof Error) {
    if (error.name === 'AbortError' || error.name === 'CanceledError') return true;
    const err = error as Error & { code?: string };
    if (axios.isAxiosError(error) && err.code === 'ERR_CANCELED') return true;
  }
  return false;
}

/**
 * Create a unified API client for REST calls.
 * Each app configures baseURL, headers, and error handling.
 */
export function createApiClient(config: CreateApiClientConfig): ApiClient {
  const api: AxiosInstance = axios.create({
    baseURL: config.baseURL,
    withCredentials: config.withCredentials ?? true,
    timeout: config.timeout ?? DEFAULT_API_TIMEOUT_MS,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (config.getRequestHeaders) {
    api.interceptors.request.use(
      (axiosConfig: InternalAxiosRequestConfig) => {
        Object.assign(axiosConfig.headers ?? {}, config.getRequestHeaders!());
        return axiosConfig;
      },
      (error: unknown) => Promise.reject(error),
    );
  }

  const { handleError } = config;

  const get = async <T = unknown>(
    url: string,
    axiosConfig?: AxiosRequestConfig,
    options?: ApiOptions,
  ): Promise<T> => {
    try {
      const response = await api.get<T>(url, {
        ...axiosConfig,
        ...(options?.timeout !== undefined ? { timeout: options.timeout } : {}),
      });
      return response.data;
    } catch (error) {
      handleError(error, options?.showErrorToast ?? true);
      throw new Error('Unreachable');
    }
  };

  const post = async <T, D>(url: string, data: D, options?: ApiOptions): Promise<T> => {
    try {
      const response = await api.post<T>(url, data, {
        ...(options?.timeout !== undefined ? { timeout: options.timeout } : {}),
        ...(options?.headers !== undefined ? { headers: options.headers } : {}),
      });
      return response.data;
    } catch (error) {
      handleError(error, options?.showErrorToast ?? true);
      throw new Error('Unreachable');
    }
  };

  const patch = async <T, D>(url: string, data: D, options?: ApiOptions): Promise<T> => {
    try {
      const response = await api.patch<T>(url, data, {
        ...(options?.timeout !== undefined ? { timeout: options.timeout } : {}),
      });
      return response.data;
    } catch (error) {
      handleError(error, options?.showErrorToast ?? true);
      throw new Error('Unreachable');
    }
  };

  const del = async <T = unknown>(url: string, options?: ApiOptions): Promise<T> => {
    try {
      const response = await api.delete<T>(url, {
        ...(options?.timeout !== undefined ? { timeout: options.timeout } : {}),
      });
      return response.data;
    } catch (error) {
      handleError(error, options?.showErrorToast ?? true);
      throw new Error('Unreachable');
    }
  };

  return { get, post, patch, delete: del };
}
